import {createRoot} from 'react-dom/client';
import {Provider} from 'react-redux';
import {NotificationsPanelDropdown} from '@Components/notifications-panel-dropdown/notifications-panel-dropdown';
import type {ReactElement} from 'react';
import {openNotificationsModal} from '@Modals/notifications-modal/notifications-modal-library';
import {NotificationButton} from '@Components/notification-button/notification-button';
import useWindowSize from '@Hooks/useWindowSize';
import {GA4EventName} from '@Libraries/ga-events';
import {useLoadI18} from '@/hooks/useLoadI18';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';

enum NavColorTheme {
  DEFAULT = 'default',
  DARK = 'dark',
  WHITE = 'white',
}

interface NotificationsRootProps {
  colorTheme: NavColorTheme;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const NotificationsRoot = ({colorTheme}: NotificationsRootProps): ReactElement => {
  const isMobile = useWindowSize().windowWidth < 550;
  const isi8nLoaded = useLoadI18();

  if (!isi8nLoaded) {
    return <Icon shape={IconShape.SQUARE} size={IconSize.SIZE_ICON_20} type={IconType.DARK_GHOST} icon={'icon-notification'} className="_cursor-wait spacing-m-r-2"></Icon>;
  }

  const buttonType = colorTheme !== NavColorTheme.DEFAULT ? IconType.GHOST : IconType.DARK_GHOST;
  return (
    <Provider store={window.PMW.redux.store}>
      {isMobile ? (
        <NotificationButton
          onClick={() => {
            openNotificationsModal({isMobile});
            window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_NAV_CLICK);
          }}
          buttonType={buttonType}
        />
      ) : (
        <NotificationsPanelDropdown buttonType={buttonType} isMobile={isMobile} />
      )}
    </Provider>
  );
};

window.PMW.mergePMW(window.PMW, {
  initNotificationsDropdown: (): void => {
    const notifRootElement = document.getElementById('js-notifications-button');
    if (!notifRootElement) {
      console.error('[Notifications] Panel root #js-notifications-button not found!');
      return;
    }
    const navElement = document.getElementById('nav');
    let colorTheme = NavColorTheme.DEFAULT;
    if (navElement?.classList.contains('-dark')) {
      colorTheme = NavColorTheme.DARK;
    } else if (navElement?.classList.contains('-white')) {
      colorTheme = NavColorTheme.WHITE;
    }
    try {
      window.PMW.notificationsRoot = window.PMW.notificationsRoot ?? createRoot(notifRootElement);
      window.PMW.notificationsRoot.render(<NotificationsRoot colorTheme={colorTheme} />);
    } catch (err) {
      console.error('[Notifications] Failed to render dropdown:', err);
    }
  },
});
